// IDE/ide/helpObject/ObjectHelp.tsx
import React, { useState } from 'react';
import { Button, Col, Modal, Row, Typography, message } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import type { LanguageId } from '../core/types'; // <- ajuste si besoin
import { TObstacle } from '../../../../models/objects/objects';
import { getImagePath } from '../../../../utils/format/valueFormat';
import { toIdentifier } from '../../../../utils/gen/strings';

const { Title, Paragraph, Text } = Typography;

interface ObjectHelpProps {
    obstacles: TObstacle[];
    open: boolean;
    onClose: () => void;
    language: LanguageId; // 'javascript' | 'python'
}

function makeGlobalSnippet(language: LanguageId) {
    if (language === 'python') {
        return {
            display: `# Pour ajouter un objet
id1 = addObject(name, positionX, positionY)`,
            copy: `# Pour ajouter un objet
id1 = addObject("name", 10, 10)`,
        };
    }
    // javascript
    return {
        display: `// Pour ajouter un objet
const id1 = addObject(name, positionX, positionY);`,
        copy: `// Pour ajouter un objet
const id1 = addObject("name", 10, 10);`,
    };
}

function makePerObstacleSnippet(obs: TObstacle, language: LanguageId) {
    const safe = toIdentifier(obs.name);
    if (language === 'python') {
        const varName = `id_${safe}`.toLowerCase();
        return `${varName} = addObject("${obs.name}", 10, 10)`;
    }
    return `const id${safe} = addObject("${obs.name}", 10, 10);`;
}

const ObjectHelp: React.FC<ObjectHelpProps> = ({ obstacles, open, onClose, language }) => {
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [globalCopied, setGlobalCopied] = useState(false);

    const { display: globalDisplay, copy: globalCopy } = makeGlobalSnippet(language);

    const handleGlobalCopy = () => {
        navigator.clipboard.writeText(globalCopy);
        setGlobalCopied(true);
        message.success('Code copié !');
        setTimeout(() => setGlobalCopied(false), 2000);
    };

    const handleCopyObstacle = (obs: TObstacle) => {
        navigator.clipboard.writeText(makePerObstacleSnippet(obs, language));
        setCopiedId(obs.id);
        message.success('Code copié !');
        setTimeout(() => setCopiedId(null), 2000);
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width="90%"
            centered
            styles={{
                body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', paddingRight: 12 },
                content: { margin: 0 },
            }}
        >
            <Title level={3}>Objets</Title>

            {/* Bloc générique */}
            <div
                style={{
                    background: '#fafafa',
                    padding: 16,
                    borderRadius: 8,
                    marginBottom: 24,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
            >
                <Paragraph>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        <code>{globalDisplay}</code>
                    </pre>
                </Paragraph>
                <Button icon={globalCopied ? <CheckOutlined /> : <CopyOutlined />} onClick={handleGlobalCopy}>
                    {globalCopied ? 'Copié' : 'Copier'}
                </Button>
            </div>

            {/* Cartes objets */}
            <Row gutter={[24, 24]} justify="start">
                {obstacles
                    .filter((obs) => obs.img !== '')
                    .map((obs) => (
                        <Col key={obs.id} xs={12} sm={8} md={6} lg={4} xl={3}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                    backgroundColor: '#fff',
                                    padding: 12,
                                    borderRadius: 8,
                                }}
                            >
                                <div
                                    style={{
                                        width: 150,
                                        height: 150,
                                        borderRadius: 8,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        marginBottom: 8,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <img
                                        src={getImagePath(obs.img)}
                                        alt={obs.name}
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                    />
                                </div>
                                <Text strong>{obs.name}</Text>
                                <Button
                                    icon={copiedId === obs.id ? <CheckOutlined /> : <CopyOutlined />}
                                    onClick={() => handleCopyObstacle(obs)}
                                    style={{ marginTop: 8 }}
                                >
                                    {copiedId === obs.id ? 'Copié' : 'Copier'}
                                </Button>
                            </div>
                        </Col>
                    ))}
            </Row>
        </Modal>
    );
};

export default ObjectHelp;
